import { Navigate, Outlet, useLocation } from 'react-router-dom'
import toast from 'react-hot-toast'
import { useAuth } from '../contexts/AuthContext'
import LoadingSpinner from './LoadingSpinner'
import { authService } from '../services/auth'

const ProtectedRoute = ({ requireVerified = false, requiredPermission = null }) => {
  const { isAuthenticated, loading, user, hasPermission } = useAuth()
  const location = useLocation()

  // Show loading spinner while checking authentication
  if (loading) {
    return <LoadingSpinner fullScreen text="Checking authentication..." />
  }

  // Redirect to login if not authenticated
  if (!isAuthenticated) {
    return <Navigate to="/login" state={{ from: location }} replace />
  }

  // Check if email verification is required
  if (requireVerified && user && !user.is_verified) {
    return (
      <div className="min-h-screen flex items-center justify-center p-4">
        <div className="max-w-md w-full bg-white rounded-lg shadow-lg p-6 text-center">
          <div className="mb-4">
            <svg
              className="w-16 h-16 text-warning-500 mx-auto"
              fill="none"
              viewBox="0 0 24 24"
              stroke="currentColor"
            >
              <path
                strokeLinecap="round"
                strokeLinejoin="round"
                strokeWidth={2}
                d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-3L13.732 4c-.77-1.333-2.694-1.333-3.464 0L3.34 16c-.77 1.333.192 3 1.732 3z"
              />
            </svg>
          </div>
          <h2 className="text-2xl font-bold text-gray-900 mb-2">
            Email Verification Required
          </h2>
          <p className="text-gray-600 mb-6">
            Please verify your email address to access this page.
            Check your inbox for a verification link.
          </p>
          <button
            onClick={() => {
              // Call resend verification email API
              toast.promise(
                authService.resendVerificationEmail(),
                {
                  loading: 'Sending verification email...',
                  success: 'Verification email sent!',
                  error: 'Failed to send email'
                }
              )
            }}
            className="btn-primary w-full"
          >
            Resend Verification Email
          </button>
        </div>
      </div>
    )
  }

  // Check if user has required permission
  if (requiredPermission && !hasPermission(requiredPermission)) {
    return <Navigate to="/unauthorized" state={{ from: location, requiredPermission }} replace />
  }

  // Render protected content
  return <Outlet />
}

export default ProtectedRoute
